import './styles.scoped.css'
import {
    Typography,
    Button,
    TextField,
    Select,
    InputAdornment,
    MenuItem,
    IconButton,
    Chip,
    Menu,
    FormControl,
    Checkbox,
    ListItemText,
    Skeleton,
    Avatar,
    CircularProgress,
    Box,
} from '@mui/material'
import {Add, Settings, Search, MoreVert, Menu as MenuIcon, FilterAlt} from '@mui/icons-material'
import {useState, useEffect, useRef} from 'react'
import {DataGrid} from '@mui/x-data-grid'
import {DataGridFooter, SkeletonContainer, TableSkeleton, TextSkeleton, ChipSkeleton, AvatarSkeleton} from '../../components'
import {accountApi} from '../../services'
import {format} from 'date-fns'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {TeamForm, TeamDetails, CancelInvitationDialog, DeactivateAccountDialog, ReactivateAccountDialog, MobileFilter} from './components'
import {useNavigate} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {getInitials, getColorFromName, parseError} from '../../utils/helpers'
import InfiniteScroll from 'react-infinite-scroll-component'
import {noResultsFound} from '../../assets'

export default () => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState(null)
    const [sortOrder, setSortOrder] = useState(null)
    const [search, setSearch] = useState(null)
    const [searchTyping, setSearchTyping] = useState(null)
    const {setLoading, setMessageBox, setMobileSidebar} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [openTeamForm, setOpenTeamForm] = useState(false)
    const [openTeamDetails, setOpenTeamDetails] = useState(false)
    const [openCancelInvitationDialog, setOpenCancelInvitationDialog] = useState(false)
    const [openDeactivateAccountDialog, setOpenDeactivateAccountDialog] = useState(false)
    const [openReactivateAccountDialog, setOpenReactivateAccountDialog] = useState(false)
    const [openMobileFilter, setOpenMobileFilter] = useState(false)
    const [hasMoreScroll, setHasMoreScroll] = useState(true)
    const [scrollStep, setScrollStep] = useState(1)
    const [scrollData, setScrollData] = useState([])
    const navigate = useNavigate()
    const statuses = [
        {id: 'pending_invitation', display_name: 'Pending Invitation'},
        {id: 'deactivated', display_name: 'Deactivated'},
        {id: 'active', display_name: 'Active'},
    ]
    const [roles, setRoles] = useState([])
    const [selectedStatuses, setSelectedStatuses] = useState(statuses.map(r => r.id))
    const [selectedRoles, setSelectedRoles] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const scrollRequestId = useRef(0)
    const {mobile} = useMobile()

    useEffect(() => {
        if (mobile) {
            setMobileSidebar(false)
        }

        accountApi
            .getRoles()
            .then(
                ({
                    data: {
                        data: {roles},
                    },
                }) => {
                    setRoles(roles)
                    setSelectedRoles(roles.map(r => r.id))
                },
            )
            .catch(error => {
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }, [])

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadData()
        }, 1000)
        return () => clearTimeout(timeout)
    }, [page, pageSize, sortBy, sortOrder, search, selectedStatuses, selectedRoles, mobile])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (mobile && scrollStep == 1 && roles && roles.length > 1) {
                loadScrollData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [scrollStep, roles, mobile])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearch(searchTyping)
        }, 1000)
        return () => clearTimeout(timeout)
    }, [searchTyping])

    // After a save the table the user is looking at stays visible under the spinner.
    const loadData = (afterSave = false) => {
        if (mobile) {
            if (scrollStep != 1) {
                scrollRequestId.current++
                setSkeleton(true)
            }
            setScrollData([])
            setScrollStep(1)
        } else {
            const id = ++requestId.current
            if (afterSave) setLoading(true)
            else setSkeleton(true)
            accountApi
                .getUsers({
                    page: page + 1,
                    limit: pageSize,
                    search: search,
                    include_roles: true,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: selectedStatuses.length === statuses.length ? null : selectedStatuses.join(','),
                    role_ids: selectedRoles.length === roles.length ? null : selectedRoles.join(','),
                })
                .then(({data: {data}}) => {
                    if (afterSave) setLoading(false)
                    if (id !== requestId.current) return
                    setRows(data?.users || [])
                    setRowCount(data?.total)
                    setSkeleton(false)
                })
                .catch(error => {
                    if (afterSave) setLoading(false)
                    if (id !== requestId.current) return
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    setSkeleton(false)
                })
        }
    }

    const loadScrollData = () => {
        const id = ++scrollRequestId.current
        if (scrollStep == 1) {
            setSkeleton(true)
        }
        accountApi
            .getUsers({
                page: scrollStep,
                limit: 10,
                search: search,
                include_roles: true,
                sort_by: sortBy,
                sort_order: sortOrder,
                status: selectedStatuses.length === statuses.length ? null : selectedStatuses.join(','),
                role_ids: selectedRoles.length === roles.length ? null : selectedRoles.join(','),
            })
            .then(({data: {data}}) => {
                if (id !== scrollRequestId.current) return
                if (data?.users) {
                    setScrollData(prev => [...prev, ...data?.users])
                }
                setHasMoreScroll(data?.users?.length > 0)
                setScrollStep(prev => ++prev)
                if (scrollStep == 1) {
                    setSkeleton(false)
                }
            })
            .catch(error => {
                if (id !== scrollRequestId.current) return
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
                if (scrollStep == 1) {
                    setSkeleton(false)
                }
            })
    }

    const statusChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            if (selectedStatuses.length === statuses.length) {
                setSelectedStatuses([])
            } else {
                setSelectedStatuses(statuses.map(r => r.id))
            }
        } else {
            setSelectedStatuses(value)
        }
    }

    const renderStatusLabel = selectedStatuses => {
        if (selectedStatuses.length === statuses.length) {
            return 'All Statuses'
        }
        return statuses
            .filter(r => selectedStatuses.includes(r.id))
            .map(r => r.display_name)
            .join(', ')
    }

    const roleChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            if (selectedRoles.length === roles.length) {
                setSelectedRoles([])
            } else {
                setSelectedRoles(roles.map(r => r.id))
            }
        } else {
            setSelectedRoles(value)
        }
    }

    const renderRoleLabel = selectedRoles => {
        if (selectedRoles.length === roles.length) {
            return 'All Roles'
        }
        return roles
            .filter(r => selectedRoles.includes(r.id))
            .map(r => r.display_name)
            .join(', ')
    }

    const sortChange = data => {
        if (data.length > 0) {
            const {field, sort} = data[0]
            setSortBy(field)
            setSortOrder(sort)
        } else {
            setSortBy(null)
            setSortOrder(null)
        }
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const editAdminDetails = () => {
        setOpenTeamDetails(true)
        menuClose()
    }

    const resendInvitation = () => {
        setLoading(true)
        accountApi
            .resendInvitation(selectedRow?.id)
            .then(() => {
                setLoading(false)
                setMessageBox({
                    status: 'success',
                    title: 'Invitation Resent',
                    description: `The invitation to ${selectedRow?.first_name} has been resent to them. They can use the link to complete their account setup.`,
                })
                menuClose()
            })
            .catch(error => {
                setLoading(false)
                menuClose()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const cancelInvitation = () => {
        setOpenCancelInvitationDialog(true)
        menuClose()
    }

    const deactivateAdmin = () => {
        setOpenDeactivateAccountDialog(true)
        menuClose()
    }

    const reactivateAdmin = () => {
        setOpenReactivateAccountDialog(true)
        menuClose()
    }

    const addNewTeam = () => {
        setSelectedRow(null)
        setOpenTeamForm(true)
    }

    const mobileReset = () => {
        setSelectedStatuses([])
        setSelectedRoles([])
        setSortBy('')
        setSortOrder('')
        setScrollData([])
        setScrollStep(1)
    }

    const onRowClick = row => {
        setSelectedRow(row)
        setOpenTeamDetails(true)
    }

    const columns = [
        {
            field: 'first_name',
            headerName: 'Team Member Name',
            flex: 2,
            renderCell: ({row}) => row?.first_name + ' ' + row?.last_name,
        },
        {field: 'email', headerName: 'Email', flex: 1},
        {
            field: 'roles',
            headerName: 'Roles',
            flex: 1,
        },
        {
            field: 'updated_at',
            headerName: 'Last Active',
            flex: 1,
            renderCell: ({row: {updated_at}}) => format(new Date(updated_at), 'MM/dd/yyyy'),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: ({value}) => {
                if (value === 'active') {
                    return <Chip label="Active" color="success" variant="filled" />
                } else if (value === 'deactivated') {
                    return <Chip label="Deactivated" color="error" variant="filled" />
                } else if (value === 'pending_invitation') {
                    return (
                        <Chip
                            label="Pending Invitation"
                            sx={{
                                bgcolor: theme => theme.palette.warning.light,
                                color: '#ffffff',
                            }}
                            variant="filled"
                        />
                    )
                }
                return null
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.4,
            sortable: false,
            renderCell: ({row}) => (
                <>
                    <IconButton
                        onClick={e => {
                            e.stopPropagation()
                            menuOpen(e, row)
                        }}>
                        <MoreVert />
                    </IconButton>
                </>
            ),
        },
    ]

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <div className="title">
                            <IconButton onClick={() => setMobileSidebar(true)}>
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" color="text.primary">
                                Team Settings
                            </Typography>
                        </div>
                    </div>
                    <br />
                    <br />
                    <br />
                    <div className="actions">
                        <Button
                            variant="outlined"
                            startIcon={<Settings />}
                            onClick={() => navigate('/team-management/role-settings')}
                            style={{flex: 1}}>
                            Role Settings
                        </Button>
                        <Button variant="contained" startIcon={<Add />} onClick={addNewTeam} style={{flex: 1}}>
                            Add New Team
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <div className="header">
                        <Typography variant="h5" color="text.primary">
                            Team Management Settings
                        </Typography>
                        <div className="actions">
                            <Button
                                variant="outlined"
                                startIcon={<Settings />}
                                onClick={() => navigate('/team-management/role-settings')}
                                style={{minWidth: 160}}>
                                Role Settings
                            </Button>
                            <Button variant="contained" startIcon={<Add />} onClick={addNewTeam} style={{minWidth: 160}}>
                                Add New Team
                            </Button>
                        </div>
                    </div>
                    <br />
                    <br />
                </>
            )}
            <div className="filter">
                <TextField
                    placeholder="Search admin name or email address ..."
                    style={{flex: 3}}
                    onChange={e => setSearchTyping(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                {!mobile && (
                    <>
                        <FormControl style={{flex: 1}}>
                            <Select
                                multiple
                                value={selectedStatuses}
                                onChange={statusChange}
                                renderValue={renderStatusLabel}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}>
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={selectedStatuses.length === statuses.length}
                                        indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < statuses.length}
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {statuses.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Checkbox checked={selectedStatuses.includes(item.id)} />
                                        <ListItemText primary={item.display_name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{flex: 1}}>
                            <Select
                                multiple
                                value={selectedRoles}
                                onChange={roleChange}
                                renderValue={renderRoleLabel}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}>
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={selectedRoles.length === roles.length}
                                        indeterminate={selectedRoles.length > 0 && selectedRoles.length < roles.length}
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {roles.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Checkbox checked={selectedRoles.includes(item.id)} />
                                        <ListItemText primary={item.display_name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
            </div>
            <br />
            {mobile ? (
                <>
                    <div className="mobile-filter" onClick={() => setOpenMobileFilter(true)}>
                        <FilterAlt color="primary" fontSize="medium" />
                        <Typography variant="buttonMedium" color="primary">
                            Filter
                        </Typography>
                    </div>
                    {skeleton && (
                        <SkeletonContainer>
                            {[0, 1, 2, 3, 4, 5].map(index => (
                                <div className="card" key={index}>
                                    <div className="top-card">
                                        <div className="left-card">
                                            <AvatarSkeleton size={32} />
                                            <div className="profile-card">
                                                <TextSkeleton width={index % 2 ? 80 : 100} />
                                                <TextSkeleton width={index % 2 ? 100 : 90} />
                                            </div>
                                        </div>
                                        <div className="right-card">
                                            <ChipSkeleton width={index % 3 ? 64 : 128} height={32} />
                                            <Skeleton variant="circular" width={24} height={24} sx={{ml: 0.5, mr: 0.25}} />
                                        </div>
                                    </div>
                                    <div className="form">
                                        <TextSkeleton width={36} />
                                        <TextSkeleton width={index % 2 ? 70 : 96} />
                                    </div>
                                    <div className="form">
                                        <TextSkeleton width={76} />
                                        <TextSkeleton width={72} />
                                    </div>
                                </div>
                            ))}
                        </SkeletonContainer>
                    )}
                    {scrollData?.length > 0 && (
                        <InfiniteScroll
                            dataLength={scrollData?.length}
                            next={loadScrollData}
                            hasMore={hasMoreScroll}
                            loader={
                                <Box display="flex" justifyContent="center" my={2}>
                                    <CircularProgress />
                                </Box>
                            }
                            endMessage={
                                <Typography align="center" sx={{mt: 2}}>
                                    All Loaded.
                                </Typography>
                            }>
                            {scrollData.map((row, index) => (
                                <div className="card" key={index} onClick={() => onRowClick(row)}>
                                    <div className="top-card">
                                        <div className="left-card">
                                            {row?.avatar_url ? (
                                                <Avatar src={row?.avatar_url} style={{width: 32, height: 32}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(row?.first_name + ' ' + row?.last_name),
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 16,
                                                    }}>
                                                    {getInitials(row?.first_name + ' ' + row?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="profile-card">
                                                <Typography variant="body2" color="text.primary" className="name">
                                                    {row?.first_name + ' ' + row?.last_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" className="name">
                                                    {row?.email}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="right-card">
                                            {row?.status == 'active' && <Chip label="Active" color="success" variant="filled" />}
                                            {row?.status == 'deactivated' && <Chip label="Deactivated" color="error" variant="filled" />}
                                            {row?.status == 'pending_invitation' && (
                                                <Chip
                                                    label="Pending Invitation"
                                                    sx={{
                                                        bgcolor: theme => theme.palette.warning.light,
                                                        color: '#ffffff',
                                                    }}
                                                    variant="filled"
                                                />
                                            )}
                                            <IconButton
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    menuOpen(e, row)
                                                }}
                                                style={{marginLeft: -8, marginRight: -6}}>
                                                <MoreVert />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <div className="form">
                                        <Typography variant="body2" color="text.primary" style={{fontWeight: 700}}>
                                            Role:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {row?.roles}
                                        </Typography>
                                    </div>
                                    <div className="form">
                                        <Typography variant="body2" color="text.primary" style={{fontWeight: 700}}>
                                            Last Active:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {format(new Date(row?.updated_at), 'MM/dd/yyyy')}
                                        </Typography>
                                    </div>
                                </div>
                            ))}
                        </InfiniteScroll>
                    )}
                </>
            ) : (
                <>
                    {skeleton && (
                        <TableSkeleton
                            rows={pageSize}
                            columns={[
                                {flex: 2, widths: ['38%', '30%', '42%', '26%', '34%']},
                                {flex: 1, widths: ['82%', '70%', '88%', '64%', '76%']},
                                {flex: 1, widths: ['40%', '55%', '35%', '48%']},
                                {flex: 1, widths: ['50%']},
                                {flex: 1, type: 'chip', chipWidth: 72},
                                {flex: 0.4, type: 'actions'},
                            ]}
                        />
                    )}
                    {rows?.length > 0 && (
                        <DataGrid
                            style={{display: skeleton ? 'none' : 'inline'}}
                            rows={rows}
                            columns={columns}
                            paginationModel={{page, pageSize}}
                            onSortModelChange={sortChange}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            onRowClick={e => onRowClick(e.row)}
                            slots={{
                                footer: () => (
                                    <DataGridFooter page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} rowCount={rowCount} />
                                ),
                            }}
                        />
                    )}
                </>
            )}
            {!skeleton && ((!mobile && rows?.length == 0) || (mobile && scrollData?.length == 0)) && (
                <>
                    <br />
                    <Typography variant="subtitle2" color="text.secondary">
                        Search Results (0)
                    </Typography>
                    <br />
                    <br />
                    <div style={{textAlign: 'center'}}>
                        <img src={noResultsFound} width={mobile ? '200' : '372'} />
                        <br />
                        <br />
                        <div style={{marginBottom: 10}}>
                            <Typography variant={mobile ? 'h7' : 'h5'} color="text.primary">
                                No Results Found
                            </Typography>
                        </div>
                        <Typography variant={mobile ? 'body2' : 'body1'} color="text.primary">
                            No team match your search criteria. Try adjusting your filters or criteria to find teams.
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                {selectedRow?.status == 'active' && (
                    <div>
                        <MenuItem onClick={editAdminDetails}>Edit Admin Details</MenuItem>
                        <MenuItem onClick={deactivateAdmin} sx={theme => ({color: theme.palette.error.main})}>
                            Deactivate Admin
                        </MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'deactivated' && (
                    <div>
                        <MenuItem onClick={editAdminDetails}>Edit Admin Details</MenuItem>
                        <MenuItem onClick={reactivateAdmin}>Reactivate Admin</MenuItem>
                    </div>
                )}
                {selectedRow?.status == 'pending_invitation' && (
                    <div>
                        <MenuItem onClick={editAdminDetails}>Edit Admin Details</MenuItem>
                        <MenuItem onClick={resendInvitation}>Resend Invitation</MenuItem>
                        <MenuItem onClick={cancelInvitation} sx={theme => ({color: theme.palette.error.main})}>
                            Cancel Invitation
                        </MenuItem>
                    </div>
                )}
            </Menu>
            <TeamForm open={openTeamForm} setOpen={setOpenTeamForm} data={selectedRow} loadData={() => loadData(true)} roles={roles} />
            <TeamDetails open={openTeamDetails} setOpen={setOpenTeamDetails} data={selectedRow} setOpenTeamForm={setOpenTeamForm} />
            <CancelInvitationDialog
                open={openCancelInvitationDialog}
                setOpen={setOpenCancelInvitationDialog}
                data={selectedRow}
                loadData={() => loadData(true)}
            />
            <DeactivateAccountDialog
                open={openDeactivateAccountDialog}
                setOpen={setOpenDeactivateAccountDialog}
                data={selectedRow}
                loadData={() => loadData(true)}
            />
            <ReactivateAccountDialog
                open={openReactivateAccountDialog}
                setOpen={setOpenReactivateAccountDialog}
                data={selectedRow}
                loadData={() => loadData(true)}
            />
            <MobileFilter
                open={openMobileFilter}
                setOpen={setOpenMobileFilter}
                statuses={statuses}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                roles={roles}
                selectedRoles={selectedRoles}
                setSelectedRoles={setSelectedRoles}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                mobileReset={mobileReset}
            />
        </>
    )
}
